import type { UseCaseConfig } from "../types";
import { CORE_FIELDS } from "../shared/form-fields";
import { USE_CASE_SEO } from "../seo-manifest";
import { hrefForUseCase } from "../links";
import { CrossLink } from "../../components/landing/CrossLink";

/** Gallery cover screenshots under `public/`. */
const GALLERY = "/gallery/local-government";

/**
 * Local Government use case. Targets the city/county office that runs a
 * report-driven program (an annual impact report, a voter-funded initiative,
 * a grant-funded team) and has to justify it again each cycle. Leads with
 * the accountability pain, not public awareness. Its narratives are live ones
 * about real places, kept in Dispatch as share links (dev), so the page shows
 * them as an "in the wild" gallery instead of a phone embed. See
 * `use-cases/local-government/brief.md`.
 */
export const localGovernmentConfig: UseCaseConfig = {
  slug: "local-government",
  menuLabel: "Local Government",
  navLabel: "Use Case · Local Government",
  teaser:
    "Turn the report that justifies your program into a narrative residents finish, and bring back what they said.",
  meta: USE_CASE_SEO["local-government"],
  hero: {
    eyebrowLabel: "Use Case",
    subhead: "Local Government",
    headlineLead: "Your report made the case. ",
    // Non-breaking spaces keep the emphasis on one line.
    headlineEmphasis: "Show\u00A0it\u00A0landed.",
    body: "Your office publishes a report to make the case for the next budget line, grant, or council vote. Then it sits on a webpage, and nobody can say who read it or what they thought. Forward turns that report into a short, mobile-first narrative residents finish, and brings back their reactions as real signal you can take to your council, budget office, or funder.",
    byline: "Built for the next budget hearing, council update, or renewal.",
  },
  gallery: {
    eyebrow: "Live Gallery",
    headline: "Samples from Atlanta, Nashville, and beyond.",
    body: "These narratives are built from reports these offices published. Open one and swipe through it the way a resident would.",
    items: [
      {
        label: "City innovation office · Annual impact",
        title: "Atlanta OTI: 2025 Impact",
        byline:
          "By Juice Analytics · From OTI’s 2025 Impact Report · 6\u00A0chapters",
        description:
          "The Atlanta Office of Technology and Innovation’s 2025: $37M+ invested across 146 companies, 1,155 new hires, what founders say is still hard, and five priorities for 2026.",
        href: "https://dev.insightsrecap.com?share=7d029078-5830-465b-8e6b-6651cd286d40",
        ctaLabel: "Open the narrative",
        image: {
          src: `${GALLERY}/atlanta-oti-cover.webp`,
          alt: "Cover screen: “A year of proof, not just paperwork.”",
          position: "50% 33%",
        },
      },
      {
        label: "Voter-funded program · Progress update",
        title: "Choose How You Move: 2026 Progress Update",
        byline:
          "By Juice Analytics · From Nashville’s CHYM progress updates · 5\u00A0chapters",
        description:
          "Nashville’s voter-approved transit program, nearly two years in: smart signals on Lebanon Pike, the city’s first queue jump lane, 86 miles of sidewalks, and more than a million fare-free trips.",
        href: "https://dev.insightsrecap.com?share=7d6dc9ee-0377-4a13-a7c5-6b2b18d358b0",
        ctaLabel: "Open the narrative",
        image: {
          src: `${GALLERY}/choose-how-you-move-cover.webp`,
          alt: "Cover screen: “Your vote is working.”",
          position: "50% 11%",
        },
      },
    ],
  },
  moments: {
    eyebrow: "Timed to the moment that matters",
    headline: "One narrative, ready for whichever decision comes next.",
    items: [
      {
        title: "Council update",
        body: "Send the link ahead of the quarterly update, so members arrive having seen the progress, not just the slide.",
      },
      {
        title: "Budget hearing",
        body: "Bring the narrative as an exhibit, along with what residents said when they read it.",
      },
      {
        title: "Grant renewal",
        body: "Show a funder the results in a form their board will get through.",
      },
      {
        title: "Voter-funded programs",
        body: "Keep residents current on what their vote is building, long before anyone asks them again.",
      },
      {
        eyebrow: "What comes back",
        title: "Real reaction",
        body: "Readers answer a question or two along the way. Their answers come back to you as a follow-up narrative: real signal from people who engaged, not a scientific survey.",
      },
    ],
  },
  scale: {
    eyebrow: "From one briefing to every budget cycle",
    leadIn: "Whether it’s ",
    firstHighlight: "one council briefing",
    middle: (
      <>
        {" "}
        or <br className="hidden md:block" />
      </>
    ),
    secondHighlight: "every budget cycle,",
    tail: (
      <>
        <br />
        the case for your work gets read.
      </>
    ),
    subtitle: (
      <>
        Launch the narrative alongside your annual report, then refresh it for
        the next decision point: a quarterly update, a mid-year check-in, the
        run-up to a renewal. Voice, look, and sourcing stay consistent every
        time.
      </>
    ),
    counters: ["Once", "Quarterly", "Each Budget", "Every Renewal"],
    footnote: (
      <>
        Publishing research or benchmark studies? See how{" "}
        <CrossLink href={hrefForUseCase("industry-reporting")} bold>
          Research Reporting
        </CrossLink>{" "}
        keeps them working all year.
      </>
    ),
  },
  pillars: {
    eyebrow: "Built for public offices",
    headline: "Three reasons your office can pull this off.",
    points: [
      {
        title: "We work from the report you already published.",
        body: "No data handoff, no IT ticket, no new platform to procure. If the report is public, we can build the narrative from it, sources and all.",
      },
      {
        title: "Real reaction, clearly labeled.",
        body: "Quizzes and a closing question capture what readers took away. It’s structured signal from residents who engaged, not a representative poll, and it’s more than a PDF has ever told you.",
      },
      {
        title: "Results come back as a narrative.",
        body: "No dashboard to log into. We deliver what readers did and said as a short follow-up narrative you can forward to your council or funder.",
      },
    ],
  },
  cta: {
    sectionEyebrow: "Your report",
    headlineLead: "What would ",
    headlineEmphasis: "your report",
    headlineTail: " look like as a narrative people finish?",
    body: "Point us at a report your office has published: an annual impact report, a progress update, a performance summary. We’ll build a sample narrative from it, working only from what’s already public. No data handoff, no pitch. Just the thing itself, so you can see what your report could become.",
    submitLabel: "Send it →",
    sendingLabel: "Sending…",
    continueLabel: "Continue →",
    success: {
      headline: "We’ll be in touch.",
      body: "Someone from our team will reach out within one to two business days, often with a sample narrative built from the report you pointed us to.",
    },
    fields: [
      ...CORE_FIELDS,
      {
        key: "report_url",
        type: "text",
        label: "Link to a report your office has published",
        placeholder: "https://yourcity.gov/annual-report-2025",
        required: false,
        core: false,
        helpText:
          "A public PDF or webpage is enough. We build from the report itself.",
      },
      {
        key: "next_decision_point",
        type: "select",
        label: "What’s the next decision your program faces?",
        required: true,
        core: false,
        allowOther: true,
        options: [
          { value: "budget-hearing", label: "A budget hearing" },
          { value: "council-update", label: "A council update" },
          { value: "grant-renewal", label: "A grant renewal" },
          { value: "ballot-measure", label: "A ballot measure or election" },
        ],
      },
    ],
  },
};
